/**
 * ScoreBreakdownPopover — focused "what does this score MEAN right now" view
 * that opens when you click the Quality or Valuation circle in StockDetail.
 * Steve directive 2026-05-22: the existing QualityAttributionModal is a
 * time-delta view (vs prior period) and its hardcoded weights are wrong;
 * he wants a now-state explainer with real pillar weights + top contributors.
 *
 * Two modes via `axis` prop:
 *   - 'quality'   → 4 pillars with real DEFAULT_PILLAR_WEIGHTS (45/20/15/20)
 *                   and a top-contributors list per pillar
 *   - 'valuation' → V_blended = 0.75·V_peer + 0.25·V_absolute_C breakdown
 *                   plus top-cheap / top-expensive multiples
 *
 * Reads scores.json shape (passed via `stock` prop). No new API call.
 */
'use client';

import { useEffect } from 'react';
import { DEFAULT_PILLAR_WEIGHTS } from '@/lib/factor-config';
import { prodQuality } from '@/lib/composite-score';

type Axis = 'quality' | 'valuation';

interface PillarScores {
  profitability?: number | null;
  growth?: number | null;
  safety?: number | null;
  shareholderReturns?: number | null;
  momentum?: number | null;
}

interface StockShape {
  symbol: string;
  name?: string;
  sector?: string;
  qualityScore?: number | null;
  qualityScoreBlended?: number | null;
  valuationScore?: number | null;
  valuationScoreAbsolute?: number | null;
  valuationScoreBlended?: number | null;
  compositeScore?: number | null;
  pillarScores?: PillarScores;
  factors?: Record<string, unknown>;
}

// Factor → human label + which multiples are valuation-relevant.
// Subset to keep popover scannable; ranked by typical model influence.
const VALUATION_FACTOR_LABELS: Record<string, string> = {
  fwdPE:        'Forward P/E',
  fwdPE2yr:     'P/E (FY+2)',
  fwdPE3yr:     'P/E (FY+3)',
  fwdEvToEbitda:'Forward EV/EBITDA',
  evToEbitda:   'Trailing EV/EBITDA',
  pegRatio:     'PEG ratio',
  priceToBook:  'Price / Book',
  fwdPtoBook:   'Forward P/B (banks)',
  pToRevenue:   'Price / Revenue',
  pToFfo:       'P/FFO (REITs)',
  pToNav:       'P/NAV (REITs)',
  ffoYield:     'FFO yield (REITs)',
};

const QUALITY_FACTOR_LABELS: Record<string, string> = {
  // Profitability
  fwdRoe:       'Forward ROE',
  fwdRoa:       'Forward ROA',
  fwdNetMargin: 'Forward net margin',
  roic:         'ROIC',
  grossProfitToAssets: 'Gross profit / assets',
  grossMargin:  'Gross margin',
  netMargin:    'Net margin',
  accrualsRatio: 'Accruals ratio',
  rotce:        'ROTCE (banks)',
  rotceDirect:  'ROTCE direct (banks)',
  nim:          'Net interest margin',
  efficiencyRatio: 'Efficiency ratio (banks)',
  provisionsToNII: 'Provisions / NII (banks)',
  ffoYield:     'FFO yield (REITs)',
  // Growth
  fwdEpsCagr2yr: 'Forward EPS 2y CAGR',
  fwdEpsCagr3yr: 'Forward EPS 3y CAGR',
  fwdRevCagr2yr: 'Forward revenue 2y CAGR',
  nextQtrEpsGrowth: 'Next-Q EPS growth',
  fwdOperLeverage: 'Forward operating leverage',
  fcfGrowth3yr: 'FCF 3y growth',
  rdIntensity:  'R&D intensity',
  // Safety
  fwdDebtToEbitda: 'Forward debt/EBITDA',
  debtToEbitda: 'Debt / EBITDA',
  currentRatio: 'Current ratio',
  beta:         'Beta',
  cdsSpread:    'CDS spread',
  shortInterestDaysToCover: 'Short interest (DTC)',
  tier1CapRatio: 'Tier 1 capital (banks)',
  nplRatio:     'Non-performing loans (banks)',
  provCoverage: 'Provision coverage (banks)',
  // Shareholder Returns
  fwdDividendYield: 'Forward dividend yield',
  buybackYield: 'Buyback yield',
  impliedBuybackYield: 'Implied buyback yield',
  netDebtPaydownYield: 'Net debt paydown yield',
  insiderNetBuy: 'Insider net buying',
  sbcDilution:  'SBC dilution',
};

function fmtZ(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}σ`;
}
function fmtNum(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

function pillarRead(z: number | null | undefined, pillarName: string): string {
  if (z == null) return `${pillarName} not scored`;
  if (z >= 2)   return `${pillarName} elite (top decile of peer group)`;
  if (z >= 1)   return `${pillarName} strong (better than 80% of peers)`;
  if (z >= 0.3) return `${pillarName} above peer median`;
  if (z >= -0.3) return `${pillarName} in line with peers`;
  if (z >= -1)  return `${pillarName} below peer median`;
  if (z >= -2)  return `${pillarName} weak (bottom quintile)`;
  return `${pillarName} extreme weakness (bottom decile)`;
}

function valuationRead(vPeer: number | null | undefined, vAbs: number | null | undefined, vBlended: number | null | undefined): string {
  const v = vBlended ?? vPeer;
  if (v == null) return 'Not scored';
  const peerStr = vPeer == null ? '—' : (vPeer >= 0 ? `cheaper than peers (+${vPeer.toFixed(2)}σ peer-z)` : `more expensive than peers (${vPeer.toFixed(2)}σ peer-z)`);
  const absStr = vAbs == null ? '' : ` · absolute lens ${vAbs >= 0 ? '+' : ''}${vAbs.toFixed(2)}σ (asymmetry + DCF + today-FV discount)`;
  return `${peerStr}${absStr}`;
}

function topFactorsForPillar(factors: Record<string, unknown> | undefined, allowedNames: string[]): Array<{ key: string; label: string; z: number }> {
  if (!factors) return [];
  return allowedNames
    .map(k => ({ key: k, label: QUALITY_FACTOR_LABELS[k] ?? k, raw: factors[k] }))
    .filter((r): r is { key: string; label: string; raw: number } => typeof r.raw === 'number' && Number.isFinite(r.raw))
    .map(r => ({ key: r.key, label: r.label, z: r.raw }));
}

// Factor sets per pillar — kept LOOSE (covers nonFinancial / financial / reit
// flavors so the popover doesn't depend on knowing the stock's peer group).
const PILLAR_FACTOR_NAMES: Record<keyof PillarScores, string[]> = {
  profitability: ['fwdRoe', 'fwdRoa', 'fwdNetMargin', 'roic', 'grossProfitToAssets', 'grossMargin', 'netMargin', 'accrualsRatio', 'rotce', 'rotceDirect', 'nim', 'efficiencyRatio', 'provisionsToNII', 'ffoYield'],
  growth:        ['fwdEpsCagr2yr', 'fwdEpsCagr3yr', 'fwdRevCagr2yr', 'nextQtrEpsGrowth', 'fwdOperLeverage', 'fcfGrowth3yr', 'rdIntensity'],
  safety:        ['fwdDebtToEbitda', 'debtToEbitda', 'currentRatio', 'beta', 'cdsSpread', 'shortInterestDaysToCover', 'tier1CapRatio', 'nplRatio', 'provCoverage'],
  shareholderReturns: ['fwdDividendYield', 'buybackYield', 'impliedBuybackYield', 'netDebtPaydownYield', 'insiderNetBuy', 'sbcDilution'],
  momentum:      [],
};

const PILLAR_LABEL: Record<keyof PillarScores, string> = {
  profitability: 'Profitability',
  growth: 'Growth',
  safety: 'Safety',
  shareholderReturns: 'Shareholder Returns',
  momentum: 'Momentum',
};

export function ScoreBreakdownPopover({
  axis,
  stock,
  onClose,
}: {
  axis: Axis;
  stock: StockShape;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center pt-16 px-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0a0e14] border border-[#1e293b] rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0e14] border-b border-[#1e293b] px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-bold text-white">
              {stock.symbol} <span className="text-[#64748b] text-[12px] font-normal">— {axis === 'quality' ? 'Quality breakdown' : 'Valuation breakdown'}</span>
            </h3>
            <div className="text-[10px] text-[#64748b] mt-0.5">
              {stock.name ?? ''} {stock.sector && `· ${stock.sector}`}
            </div>
          </div>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-white text-[14px] px-2" aria-label="Close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {axis === 'quality' ? <QualityBody stock={stock} /> : <ValuationBody stock={stock} />}
        </div>
      </div>
    </div>
  );
}

function QualityBody({ stock }: { stock: StockShape }) {
  const q = prodQuality(stock);
  const p = stock.pillarScores ?? {};
  const pillars: Array<{ key: keyof PillarScores; weight: number }> = [
    { key: 'profitability',      weight: DEFAULT_PILLAR_WEIGHTS.profitability },
    { key: 'growth',             weight: DEFAULT_PILLAR_WEIGHTS.growth },
    { key: 'safety',             weight: DEFAULT_PILLAR_WEIGHTS.safety },
    { key: 'shareholderReturns', weight: DEFAULT_PILLAR_WEIGHTS.shareholderReturns },
  ];
  // Contribution = pillar_z × weight%. Quality blends these with the weights
  // shown; this is the readable "what drove Q" decomposition.
  const contributions = pillars.map(({ key, weight }) => {
    const z = p[key];
    const contrib = (z ?? 0) * (weight / 100);
    return { key, weight, z, contrib };
  });
  return (
    <>
      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">Quality score (Q)</div>
            <div className={`text-[28px] font-bold tabular-nums ${
              (q ?? 0) >= 1.5 ? 'text-emerald-300' :
              (q ?? 0) >= 0.3 ? 'text-emerald-400/80' :
              (q ?? 0) >= -0.3 ? 'text-[#cbd5e1]' :
              (q ?? 0) >= -1.5 ? 'text-amber-300' :
              'text-red-300'
            }`}>
              {fmtZ(q)}
            </div>
          </div>
          <div className="text-[11px] text-[#cbd5e1] italic max-w-[60%] text-right leading-snug">
            {pillarRead(q, 'Overall quality')}.
          </div>
        </div>
      </div>

      {pillars.map(({ key, weight }, i) => {
        const z = p[key];
        const contrib = contributions[i].contrib;
        const factors = topFactorsForPillar(stock.factors, PILLAR_FACTOR_NAMES[key]);
        const top = [...factors].sort((a, b) => b.z - a.z).slice(0, 3);
        const bot = [...factors].sort((a, b) => a.z - b.z).slice(0, 3);
        return (
          <div key={key} className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#cbd5e1] font-semibold">
                  {PILLAR_LABEL[key]} <span className="text-[#64748b] font-normal ml-1">{weight}% weight</span>
                </div>
                <div className="text-[11px] text-[#94a3b8] italic mt-0.5">{pillarRead(z, PILLAR_LABEL[key])}.</div>
              </div>
              <div className="text-right">
                <div className={`text-[18px] font-bold tabular-nums ${
                  (z ?? 0) >= 1 ? 'text-emerald-300' :
                  (z ?? 0) >= 0.3 ? 'text-emerald-400/80' :
                  (z ?? 0) >= -0.3 ? 'text-[#cbd5e1]' :
                  (z ?? 0) >= -1 ? 'text-amber-300' :
                  'text-red-300'
                }`}>{fmtZ(z)}</div>
                <div className="text-[9px] text-[#64748b] tabular-nums">contributes {fmtNum(contrib)} to Q</div>
              </div>
            </div>
            {factors.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-3 text-[10px]">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 mb-1">Top contributors</div>
                  <ul className="space-y-0.5">
                    {top.length === 0 ? <li className="text-[#64748b] italic">—</li> : top.map(f => (
                      <li key={f.key} className="grid grid-cols-[1fr_auto] gap-2">
                        <span className="text-[#cbd5e1] truncate">{f.label}</span>
                        <span className={`tabular-nums font-mono ${f.z >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(f.z, 1)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-red-300/70 mb-1">Biggest drags</div>
                  <ul className="space-y-0.5">
                    {bot.length === 0 ? <li className="text-[#64748b] italic">—</li> : bot.map(f => (
                      <li key={f.key} className="grid grid-cols-[1fr_auto] gap-2">
                        <span className="text-[#cbd5e1] truncate">{f.label}</span>
                        <span className={`tabular-nums font-mono ${f.z >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(f.z, 1)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="text-[10px] text-[#64748b] italic leading-snug pt-1">
        Quality = weighted average of pillar z-scores (45 / 20 / 15 / 20). Each
        factor is z-scored within the stock&rsquo;s peer group (standard /
        financial / insurance / energy / utility / REIT) and clamped [-3, +3].
        Z-scores above +1 = top quintile; below -1 = bottom quintile.
      </div>
    </>
  );
}

function ValuationBody({ stock }: { stock: StockShape }) {
  const vPeer = stock.valuationScore ?? null;
  const vAbs = stock.valuationScoreAbsolute ?? null;
  const vBlended = stock.valuationScoreBlended ?? null;
  const blendedFromComponents = (vPeer != null && vAbs != null) ? (0.75 * vPeer + 0.25 * vAbs) : null;
  const finalV = vBlended ?? blendedFromComponents ?? vPeer;
  const factors = stock.factors ?? {};
  const valFactors = Object.entries(VALUATION_FACTOR_LABELS)
    .map(([k, label]) => ({ key: k, label, z: factors[k] }))
    .filter((r): r is { key: string; label: string; z: number } => typeof r.z === 'number' && Number.isFinite(r.z));
  const cheap = [...valFactors].sort((a, b) => b.z - a.z).slice(0, 5);
  const expensive = [...valFactors].sort((a, b) => a.z - b.z).slice(0, 5);

  return (
    <>
      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">Valuation score (V_blended)</div>
            <div className={`text-[28px] font-bold tabular-nums ${
              (finalV ?? 0) >= 1.5 ? 'text-emerald-300' :
              (finalV ?? 0) >= 0.3 ? 'text-emerald-400/80' :
              (finalV ?? 0) >= -0.3 ? 'text-[#cbd5e1]' :
              (finalV ?? 0) >= -1.5 ? 'text-amber-300' :
              'text-red-300'
            }`}>{fmtZ(finalV)}</div>
          </div>
          <div className="text-[11px] text-[#cbd5e1] italic max-w-[60%] text-right leading-snug">
            {valuationRead(vPeer, vAbs, vBlended)}.
          </div>
        </div>
      </div>

      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
        <div className="text-[11px] uppercase tracking-wider text-[#cbd5e1] font-semibold mb-2">V_blended formula</div>
        <div className="grid grid-cols-[1fr_auto] gap-2 text-[12px] tabular-nums font-mono">
          <div className="text-[#cbd5e1]">Peer-z V (75% weight) — vs sector peers on 9 multiples</div>
          <div className={`${(vPeer ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(vPeer)}</div>
          <div className="text-[#cbd5e1]">Absolute V (25% weight) — asymmetry + DCF + today-FV discount</div>
          <div className={`${(vAbs ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(vAbs)}</div>
          <div className="text-[#94a3b8] border-t border-[#1e293b] pt-1.5">= 0.75 × peer-z + 0.25 × absolute</div>
          <div className={`border-t border-[#1e293b] pt-1.5 font-semibold ${(finalV ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(finalV)}</div>
        </div>
      </div>

      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
        <div className="text-[11px] uppercase tracking-wider text-[#cbd5e1] font-semibold mb-2">Multiple-level peer-z (top 5 + bottom 5)</div>
        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 mb-1">Cheapest vs peers</div>
            <ul className="space-y-0.5">
              {cheap.length === 0 ? <li className="text-[#64748b] italic">—</li> : cheap.map(f => (
                <li key={f.key} className="grid grid-cols-[1fr_auto] gap-2">
                  <span className="text-[#cbd5e1] truncate">{f.label}</span>
                  <span className={`tabular-nums font-mono ${f.z >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(f.z, 1)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-red-300/70 mb-1">Most expensive vs peers</div>
            <ul className="space-y-0.5">
              {expensive.length === 0 ? <li className="text-[#64748b] italic">—</li> : expensive.map(f => (
                <li key={f.key} className="grid grid-cols-[1fr_auto] gap-2">
                  <span className="text-[#cbd5e1] truncate">{f.label}</span>
                  <span className={`tabular-nums font-mono ${f.z >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmtZ(f.z, 1)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-[#64748b] italic leading-snug pt-1">
        Higher V = cheaper. Peer-z compares each multiple within the stock&rsquo;s
        peer group (so a tech P/E of 30 isn&rsquo;t penalized like a utility P/E
        of 30). Absolute lens (asymmetry, DCF discount, today-FV discount) adds
        a 25% universe-wide anchor so dirt-cheap-in-an-expensive-sector still
        counts. V_blended is the production V used by alpha basket selection.
      </div>
    </>
  );
}
